import * as React from 'react';

import type { CameraInfo } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/camera_local_api_pb';
import { StreamProfile } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/camera_local_api_pb';
import {
  CameraStream,
  type CameraStreamFloodlight,
  type CameraStreamHandle,
  type CameraStreamPTZ,
  type CameraStreamSiren,
  Col,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $remoteCameraDetail } from '../../../_state/cameraDetail.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { CameraDetailRecord } from '../../../types/sidecar';

import {
  CameraSnapshotButton,
  CameraSnapshotGallery,
  CameraSnapshotOverlayEditor,
  createDefaultTextLayerDraft,
  type SnapshotLayerDraft,
  useCameraSnapshot,
} from './CameraSnapshotCapture';

import { useCameraLocalClient } from '~/shared/providers/libp2p/useCameraLocalClient';
import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

type Props = {
  camera: CameraDetailRecord;
};

//
// ⚛️ ⚛️ ⚛️ CAMERA TAB LIVE FEED ⚛️ ⚛️ ⚛️
//
export const CameraTabLiveFeed: React.FC<Props> = ({ camera }) => {
  const t = useTranslations();
  const cameraStreamRef = React.useRef<CameraStreamHandle>(null);

  const [cameraInfo, setCameraInfo] = React.useState<CameraInfo | null>(null);
  const [floodlightOn, setFloodlightOn] = React.useState(false);
  const [floodlightBrightness] = React.useState(100);
  const [snapshotLayers, setSnapshotLayers] = React.useState<
    SnapshotLayerDraft[]
  >([createDefaultTextLayerDraft()]);

  const { refetch: refetchCameraDetail } = useAtomValue($remoteCameraDetail);

  const presenceMembers = useHardwarePresenceMembers();
  const isOnline = presenceMembers.has(camera.id);

  const { isConnecting } = useHardwareSidecarConnection({
    sidecar: camera.sidecarDetail,
    onConnectionFailed: refetchCameraDetail,
  });

  const cameraClient = useCameraLocalClient(camera.sidecarId);
  const snapshot = useCameraSnapshot({
    cameraId: camera.id,
    cameraClient,
    layers: snapshotLayers,
  });

  React.useEffect(() => {
    if (!cameraClient) return;

    let cancelled = false;

    cameraClient
      .getCameraInfo({ cameraId: camera.id })
      .then((response) => {
        if (cancelled || !response.camera) return;
        setCameraInfo(response.camera);
      })
      .catch((err) => {
        console.warn('Failed to get camera info via RPC', err);
      });

    return () => {
      cancelled = true;
    };
  }, [cameraClient, camera.id]);

  const negotiateStream = React.useCallback(
    async (ch: number, sdpOffer: string) => {
      if (!cameraClient) return '';
      const profileEnum =
        cameraInfo?.streamProfile === 'main'
          ? StreamProfile.MAIN
          : StreamProfile.SUB;
      const response = await cameraClient.startCameraStream({
        cameraId: camera.id,
        sdpOffer,
        channel: ch,
        streamProfile: profileEnum,
      });
      return response.sdpAnswer;
    },
    [cameraClient, camera.id, cameraInfo?.streamProfile],
  );

  const ptz = React.useMemo((): CameraStreamPTZ | undefined => {
    if (!camera.hasPtz || !cameraClient) return undefined;
    return {
      onMove: (pan, tilt, zoom) => {
        cameraClient
          .cameraPTZMove({ cameraId: camera.id, pan, tilt, zoom, speed: 0.5 })
          .catch(Function.prototype as () => void);
      },
      onStop: () => {
        cameraClient
          .cameraPTZStop({ cameraId: camera.id })
          .catch(Function.prototype as () => void);
      },
    };
  }, [cameraClient, camera.id, camera.hasPtz]);

  const floodlight = React.useMemo((): CameraStreamFloodlight | undefined => {
    if (!camera.hasFloodlight || !cameraClient) return undefined;
    return {
      enabled: floodlightOn,
      onToggle: () => {
        const newState = !floodlightOn;
        setFloodlightOn(newState);
        cameraClient
          .setFloodlight({
            cameraId: camera.id,
            enabled: newState,
            brightness: floodlightBrightness,
          })
          .catch(() => setFloodlightOn(!newState));
      },
    };
  }, [
    cameraClient,
    camera.id,
    camera.hasFloodlight,
    floodlightOn,
    floodlightBrightness,
  ]);

  const siren = React.useMemo((): CameraStreamSiren | undefined => {
    if (!camera.hasSiren || !cameraClient) return undefined;
    return {
      onTrigger: () => {
        cameraClient
          .triggerSiren({ cameraId: camera.id, times: 1 })
          .catch(Function.prototype as () => void);
      },
    };
  }, [cameraClient, camera.id, camera.hasSiren]);

  const isStreamReady =
    !!cameraClient &&
    !!cameraInfo &&
    isOnline &&
    cameraInfo.phase !== 'wrong_credentials' &&
    (cameraInfo.phase === 'operational' || cameraInfo.phase === '');

  const channelCount = cameraInfo?.channelCount || 1;

  const streamStatusMessage = React.useMemo(() => {
    if (!cameraClient) {
      return isConnecting
        ? t.hardwareView.cameraDetailView.liveFeed.connecting
        : t.hardwareView.cameraDetailView.liveFeed.connectToSidecar;
    }
    if (!isOnline) {
      return t.hardwareView.cameraDetailView.liveFeed.cameraOffline;
    }
    if (cameraInfo?.phase === 'wrong_credentials') {
      return t.hardwareView.cameraDetailView.messages.wrongCredentialsLiveFeed;
    }
    if (
      cameraInfo &&
      cameraInfo.phase !== 'operational' &&
      cameraInfo.phase !== ''
    ) {
      return t.hardwareView.cameraDetailView.messages.cameraSetupInProgress;
    }
    return null;
  }, [cameraClient, isConnecting, isOnline, cameraInfo, t]);

  return (
    <Col gap4 className="@CameraTabLiveFeed p-5">
      {cameraClient && (
        <CameraSnapshotButton
          isCapturing={snapshot.isCapturing}
          onCapture={snapshot.handleCapture}
        />
      )}

      {cameraClient && (
        <CameraSnapshotOverlayEditor
          layers={snapshotLayers}
          onChange={setSnapshotLayers}
        />
      )}

      {streamStatusMessage ? (
        <Col xCenter yCenter className="h-96 text-neutral/60">
          {streamStatusMessage}
        </Col>
      ) : (
        <CameraStream
          ref={cameraStreamRef}
          channelCount={channelCount}
          negotiateStream={negotiateStream}
          ptz={ptz}
          floodlight={floodlight}
          siren={siren}
          disabled={!isStreamReady}
        />
      )}

      <CameraSnapshotGallery
        snapshots={snapshot.snapshots}
        error={snapshot.error}
        onDownload={snapshot.handleDownload}
        onDismiss={snapshot.handleDismiss}
      />
    </Col>
  );
};
